import React, { useEffect, useMemo } from "react";
import { Box, Divider, Typography } from "@mui/material";
import { useForm, RegisterOptions, Path } from "react-hook-form";
import {
  CustomSectionTitle,
  CustomTextInput,
  CustomSingleSelect,
  CustomFileUploader,
  FixedActionBar,
  CustomLoading,
  CustomFormNavigationGuard,
  CustomChip,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import { fetchDetailedClient, updateDetailedClient } from "@/store/clientSlice";
import { fetchDetailedEmployee } from "@/store/employeeSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { getClientID, getEmployeeID } from "@/utils/authUtils";
import { useCan } from "@/hooks/useCan";
import { processClientFileArray } from "@/utils/fileUpload";

type LogoUrlItem = File | string | { data: string; id?: string; isUrl?: boolean; originalFileName?: string; mimeType?: string };

type FormValues = {
  client_name: string;
  client_name_kana: string;
  client_tell: string;
  client_mail_address: string;
  hp_address: string;
  real_estate_number: string;
  postal_code: string;
  prefecture: string;
  city: string;
  steet_address: string;
  building: string;
  logo_url: LogoUrlItem[];
  service_ids: string[];
};

const CompanySetting: React.FC = () => {
  const leftInputContainerWidth = "600px";
  const labelWidth = "120px";
  const dispatch = useDispatch<AppDispatch>();
  const clientId = getClientID();
  const can = useCan();
  const canEdit = can("client:edit");
  const loggedInEmployeeId = getEmployeeID();
  const detailClientData = useSelector(
    (state: RootState) => state.clients?.detailed?.data
  );
  const isFetching = useSelector(
    (state: RootState) => state.clients?.detailed?.isFetching ?? false
  );
  const isUpdating = useSelector(
    (state: RootState) => state.clients?.detailed?.isUpdating ?? false
  );
  const { showToast } = useGlobalToast();

  const { data: employeeData } = useSelector(
    (state: any) => state.employees.detailed
  );

  const area = useMemo(
    () => [
      { value: "01", label: "北海道" },
      { value: "02", label: "青森県" },
      { value: "03", label: "岩手県" },
      { value: "04", label: "宮城県" },
      { value: "05", label: "秋田県" },
      { value: "06", label: "山形県" },
      { value: "07", label: "福島県" },
      { value: "08", label: "茨城県" },
      { value: "09", label: "栃木県" },
      { value: "10", label: "群馬県" },
      { value: "11", label: "埼玉県" },
      { value: "12", label: "千葉県" },
      { value: "13", label: "東京都" },
      { value: "14", label: "神奈川県" },
      { value: "15", label: "新潟県" },
      { value: "16", label: "富山県" },
      { value: "17", label: "石川県" },
      { value: "18", label: "福井県" },
      { value: "19", label: "山梨県" },
      { value: "20", label: "長野県" },
      { value: "21", label: "岐阜県" },
      { value: "22", label: "静岡県" },
      { value: "23", label: "愛知県" },
      { value: "24", label: "三重県" },
      { value: "25", label: "滋賀県" },
      { value: "26", label: "京都府" },
      { value: "27", label: "大阪府" },
      { value: "28", label: "兵庫県" },
      { value: "29", label: "奈良県" },
      { value: "30", label: "和歌山県" },
      { value: "31", label: "鳥取県" },
      { value: "32", label: "島根県" },
      { value: "33", label: "岡山県" },
      { value: "34", label: "広島県" },
      { value: "35", label: "山口県" },
      { value: "36", label: "徳島県" },
      { value: "37", label: "香川県" },
      { value: "38", label: "愛媛県" },
      { value: "39", label: "高知県" },
      { value: "40", label: "福岡県" },
      { value: "41", label: "佐賀県" },
      { value: "42", label: "長崎県" },
      { value: "43", label: "熊本県" },
      { value: "44", label: "大分県" },
      { value: "45", label: "宮崎県" },
      { value: "46", label: "鹿児島県" },
      { value: "47", label: "沖縄県" },
    ],
    []
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty },
  } = useForm<FormValues>({
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: {
      client_name: "",
      client_name_kana: "",
      client_tell: "",
      client_mail_address: "",
      hp_address: "",
      real_estate_number: "",
      postal_code: "",
      prefecture: "",
      city: "",
      steet_address: "",
      building: "",
      logo_url: [],
      service_ids: [],
    },
  });

  const formValues = watch();

  const mapApiDataToFormValues = (
    apiData: Partial<FormValues> & { postcode?: string; logo_url?: string | string[]; steet_address?: string; client_services?: any[] }
  ): FormValues => {
    return {
      client_name: apiData.client_name || "",
      client_name_kana: apiData.client_name_kana || "",
      client_tell: apiData.client_tell || "",
      client_mail_address: apiData.client_mail_address || "",
      hp_address: apiData.hp_address || "",
      postal_code: apiData.postcode || "",
      prefecture: apiData.prefecture || "",
      city: apiData.city || "",
      steet_address: apiData.steet_address || "",
      building: apiData.building || "",
      real_estate_number: apiData.real_estate_number || "",
      logo_url: apiData.logo_url ? (Array.isArray(apiData.logo_url) ? apiData.logo_url : [apiData.logo_url]) : [],
      service_ids: apiData.client_services?.map((cs: any) => cs.service.id) || [],
    };
  };

  useEffect(() => {
    if (clientId) {
      void dispatch(fetchDetailedClient(clientId));
    }
    if (loggedInEmployeeId) {
      void dispatch(fetchDetailedEmployee(loggedInEmployeeId));
    }
  }, [clientId, loggedInEmployeeId, dispatch]);

  const customRegister = (name: keyof FormValues, options?: RegisterOptions<FormValues, any>) => {
    if (name === "postal_code") {
      return register(name, {
        ...options,
        pattern: {
          value: /^\d{7}$/,
          message: "郵便番号は7桁の数字で入力してください",
        },
        validate: (value) => {
          if (!value) return true;
          if (value.length !== 7) {
            return "郵便番号は7桁で入力してください";
          }
          if (!/^\d+$/.test(value)) {
            return "郵便番号は数字のみで入力してください";
          }
          return true;
        },
      } as any);
    }
    return register(name, options as any);
  };

  useEffect(() => {
    if (detailClientData && Object.keys(detailClientData).length > 0) {
      const formValues = mapApiDataToFormValues(detailClientData);
      reset(formValues);
    }
  }, [detailClientData, reset]);

  const onSubmit = async (data: FormValues) => {
    if (!canEdit) {
      showToast({ type: "error", message: "更新権限がありません" });
      return;
    }
    try {
      const formData = new FormData();
      formData.append("client_name", data.client_name || "");
      formData.append("client_name_kana", data.client_name_kana || "");
      formData.append("client_tell", data.client_tell || "");
      formData.append("client_mail_address", data.client_mail_address || "");
      formData.append("hp_address", data.hp_address || "");
      formData.append("postcode", data.postal_code || "");
      formData.append("prefecture", data.prefecture || "");
      formData.append("city", data.city || "");
      formData.append("steet_address", data.steet_address || "");
      formData.append("building", data.building || "");
      formData.append("real_estate_number", data.real_estate_number || "");

      // ロゴ画像のアップロード（Presigned URL方式）
      if (Array.isArray(data.logo_url)) {
        if (data.logo_url.length > 0) {
          const s3Keys = await processClientFileArray(data.logo_url);
          if (s3Keys.length > 0) {
            formData.append(
              "logo_url",
              JSON.stringify(s3Keys.map((key) => ({ data: key })))
            );
          }
        } else {
          // 空配列の場合は明示的に空配列を送信（削除を意味する）
          formData.append("logo_url", JSON.stringify([]));
        }
      }

      if (!clientId) {
        showToast({
          type: "error",
          message: "クライアントIDが見つかりません。再ログインしてください。",
        });
        return;
      }

      const updateResult = await dispatch(
        updateDetailedClient({ id: clientId, payload: formData })
      );

      if (updateDetailedClient.fulfilled.match(updateResult)) {
        const newFormData = mapApiDataToFormValues(updateResult.payload);
        reset(newFormData);
        showToast({ type: "success", message: "更新が完了しました" });
      } else if (updateDetailedClient.rejected.match(updateResult)) {
        const errorPayload = updateResult.payload as { message?: string; error?: string; data?: { message?: string } } | string | undefined;
        const errorMessage =
          (typeof errorPayload === "object" && errorPayload !== null
            ? errorPayload?.message || errorPayload?.error || errorPayload?.data?.message
            : undefined) ||
          (typeof errorPayload === "string" ? errorPayload : undefined) ||
          "更新に失敗しました";
        console.error("更新エラー詳細:", {
          error: updateResult.error,
          payload: errorPayload,
          type: updateResult.type,
        });
        showToast({ type: "error", message: errorMessage });
      } else {
        showToast({ type: "error", message: "更新に失敗しました" });
      }
    } catch (error) {
      const errorMessage =
        (error && typeof error === "object" && "response" in error
          ? (error.response as { data?: { message?: string } })?.data?.message
          : undefined) ||
        (error && typeof error === "object" && "message" in error
          ? (error as { message: string }).message
          : undefined) ||
        "更新に失敗しました";
      console.error("更新エラー:", error);
      showToast({ type: "error", message: errorMessage });
    }
  };

  if (isFetching) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "calc(100vh - 200px)",
          width: "100%",
        }}
      >
        <CustomLoading loading={true} fullScreen={false} size="medium" />
      </Box>
    );
  }

  return (
    <CustomFormNavigationGuard
      hasFormChanged={isDirty}
      isSubmitting={isUpdating}
    >
      {() => (
        <Box>
          <Box>
            <form
              onSubmit={handleSubmit(onSubmit)}
              style={{ display: "flex", flexDirection: "column" }}
            >
          <Box sx={{ py: { xs: 0, sm: 3 } }}>
            <CustomSectionTitle title="基本情報" addBorder={false} />
            <Divider />

            <Box
              sx={{
                mt: 2,
                maxWidth: leftInputContainerWidth,
              }}
            >
              <CustomTextInput
                label="法人名"
                name="client_name"
                type="text"
                placeholder=""
                register={register}
                errors={errors}
                isRequired
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
              <CustomTextInput
                label="フリガナ"
                name="client_name_kana"
                type="text"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
              <CustomTextInput
                label="電話番号"
                name="client_tell"
                type="phoneNumber"
                placeholder="12345678900(ハイフンなし)"
                register={register}
                errors={errors}
                isRequired
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
              {/* TODO: 一時非表示 - メールアドレス */}
              <Box sx={{ display: "none" }}>
                <CustomTextInput
                  label="メールアドレス"
                  name="client_mail_address"
                  type="email"
                  placeholder=""
                  register={register}
                  errors={errors}
                  labelWidth={labelWidth}
                  labelWidthSp="47%"
                  inputWidth={leftInputContainerWidth}
                  inputWidthSp={"100%"}
                  disabled={!canEdit}
                />
              </Box>
              <CustomTextInput
                label="HPアドレス"
                name="hp_address"
                type="text"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
              <CustomTextInput
                label="宅建業者番号"
                name="real_estate_number"
                type="text"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
            </Box>
          </Box>

          <Box sx={{ mb: 5 }}>
            <CustomSectionTitle title="ロゴ画像" />
            <Box sx={{ mt: "18px", maxWidth: leftInputContainerWidth }}>
              <CustomFileUploader
                name="logo_url"
                setValue={setValue}
                register={register}
                type="single"
                initialImages={detailClientData?.logo_url || []}
                onImagesDeleted={() => {}}
                onImageChange={() => {}}
                label="アップロード"
                errors={errors}
                enablePanzoom={true}
                rejectPdf={true}
                disabled={!canEdit}
              />
              <Box
                sx={{
                  mt: "9px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}
              >
                <Box
                  component="span"
                  sx={{
                    fontSize: "12px",
                    color: "#666",
                    fontStyle: "italic",
                    "&::before": { content: '"※"', marginRight: "4px" },
                  }}
                >
                  JPEG、PNG、JPG形式のみ対応
                </Box>
              </Box>
            </Box>
          </Box>

          <Box sx={{ mb: 5 }}>
            <CustomSectionTitle title="利用サービス" addBorder={false} />
            <Divider sx={{ borderColor: "#D9D9D9", py: { xs: 0, sm: 0.5 } }} />

            <Box
              sx={{
                mt: { xs: 1, sm: 2 },
                maxWidth: leftInputContainerWidth,
              }}
            >
              <Typography
                sx={{
                  fontSize: "14px",
                  color: "#333",
                }}
              >
                {detailClientData?.client_services?.map((cs: any) => cs.service.name).join("，") || ""}
              </Typography>
            </Box>
          </Box>

          {(() => {
            // 使用しているオプションが1件もなければセクションごとトルツメ
            const clientOptions = detailClientData?.client_options ?? [];
            if (clientOptions.length === 0) return null;
            return (
              <Box sx={{ mb: 5 }}>
                <CustomSectionTitle title="利用オプション" addBorder={false} />
                <Divider sx={{ borderColor: "#D9D9D9", py: { xs: 0, sm: 0.5 } }} />

                <Typography
                  sx={{
                    mt: { xs: 1, sm: 2 },
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#333",
                  }}
                >
                  AI-BPO
                </Typography>

                <Box
                  sx={{
                    mt: 1,
                    maxWidth: leftInputContainerWidth,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1.5,
                  }}
                >
                  {clientOptions.map((opt: any, index: number) => (
                    <Box
                      key={`${opt.option_name}-${opt.service_name}-${index}`}
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Typography
                        sx={{ fontSize: "14px", lineHeight: 1.7, color: "#333" }}
                      >
                        {opt.option_name}
                      </Typography>
                      {/* サービスタグ（/setting/account のサービスタグと同じデザイン） */}
                      <CustomChip
                        label={opt.service_name}
                        backgroundColor="rgb(240, 240, 240)"
                        color="rgb(62, 62, 62)"
                        fontSize="12px"
                        fontWeight={400}
                        sx={{ borderRadius: "6px" }}
                      />
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })()}

          <Box sx={{ mb: 8 }}>
            <CustomSectionTitle title="住所" addBorder={false} />
            <Divider sx={{ borderColor: "#D9D9D9", py: { xs: 0, sm: 0.5 } }} />

            <Box
              sx={{
                mt: { xs: 1, sm: 2 },
                maxWidth: leftInputContainerWidth,
              }}
            >
              <CustomTextInput
                label="郵便番号"
                name="postal_code"
                type="text"
                placeholder="例: 1234567"
                register={customRegister}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
              <CustomSingleSelect
                label="都道府県"
                name="prefecture"
                placeholder="都道府県を選択"
                control={control}
                errors={errors}
                options={area}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                searchable
                searchPlaceholder="検索"
                disabled={!canEdit}
                sx={{
                  "& .MuiInputBase-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                }}
              />
              <CustomTextInput
                label="市区町村"
                name="city"
                type="text"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
              <CustomTextInput
                label="番地・区画"
                name="steet_address"
                type="text"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
              <CustomTextInput
                label="建物名、部屋番号"
                name="building"
                type="text"
                placeholder=""
                register={register}
                errors={errors}
                labelWidth={labelWidth}
                labelWidthSp="47%"
                inputWidth={leftInputContainerWidth}
                inputWidthSp={"100%"}
                disabled={!canEdit}
                sx={{
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                  },
                  "& .MuiInputBase-root.Mui-disabled, & .MuiOutlinedInput-root.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    borderRadius: "8px !important",
                    overflow: "hidden !important",
                    opacity: "1 !important",
                    "&:hover": {
                      backgroundColor: "rgb(245, 245, 245) !important",
                      opacity: "1 !important",
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#D9D9D9 !important",
                      borderRadius: "8px !important",
                    },
                  },
                  "& input:disabled, & .MuiInputBase-input.Mui-disabled, & .MuiOutlinedInput-input.Mui-disabled": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    color: "rgb(62, 62, 62) !important",
                    WebkitTextFillColor: "rgb(62, 62, 62) !important",
                    borderRadius: "8px !important",
                  },
                  "& input:disabled:hover": {
                    backgroundColor: "rgb(245, 245, 245) !important",
                    opacity: "1 !important",
                  },
                }}
              />
            </Box>
          </Box>

          {canEdit && (
            <FixedActionBar
              submitLabel="更新"
              isLoading={isUpdating}
              disabled={!isDirty || isUpdating}
            />
          )}
            </form>
          </Box>
        </Box>
      )}
    </CustomFormNavigationGuard>
  );
};

export default CompanySetting;
